import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../components/Layout/LanguageContext';

const TermsSection = styled.section`
  min-height: 100vh;
  padding: 100px 2rem;
  background: var(--background);
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled(motion.h1)`
  font-size: clamp(36px, 6vw, 60px);
  margin-bottom: 2rem;
  text-align: center;
  color: #e6f1ff;
  font-weight: 600;
`;

const Content = styled(motion.div)`
  background: rgba(35, 53, 84, 0.5);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 105, 180, 0.1);
  border-radius: 16px;
  padding: 2rem;

  h2 {
    color: var(--primary);
    font-size: 1.8rem;
    margin: 2rem 0 1rem;

    &:first-child {
      margin-top: 0;
    }
  }

  p {
    color: var(--text-secondary);
    margin-bottom: 1rem;
    line-height: 1.6;
  }

  ul {
    color: var(--text-secondary);
    margin-bottom: 1rem;
    padding-left: 1.5rem;

    li {
      margin-bottom: 0.5rem;
    }
  }
`;

const termsContent = {
  de: {
    title: 'Allgemeine Geschäftsbedingungen',
    sections: [
      { heading: '1. Geltungsbereich', text: 'Diese Allgemeinen Geschäftsbedingungen gelten für alle gegenwärtigen und zukünftigen Geschäftsbeziehungen zwischen BrainMotion.ai und meinen Kunden.' },
      { heading: '2. Vertragsschluss', text: 'Der Vertrag kommt durch die Annahme der Kundenbestellung durch mich zustande. Die Annahme kann entweder schriftlich oder durch Auslieferung der Ware bzw. Erbringung der Dienstleistung erklärt werden.' },
      { heading: '3. Leistungsumfang', text: 'Der Umfang der vertraglichen Leistungen ergibt sich aus der jeweiligen Produktbeschreibung zum Zeitpunkt der Bestellung bzw. dem individuellen Angebot.' },
      { heading: '4. Preise und Zahlungsbedingungen', text: 'Alle Preise verstehen sich in Euro zzgl. der gesetzlichen Mehrwertsteuer. Zahlungen sind innerhalb von 14 Tagen nach Rechnungsstellung ohne Abzug fällig.' },
      { heading: '5. Lieferung und Leistungszeit', text: 'Die Lieferung erfolgt an die vom Kunden angegebene Lieferadresse. Liefertermine sind unverbindlich, soweit nicht ausdrücklich etwas anderes vereinbart wurde.' },
      { heading: '6. Nutzungsrechte', text: 'Mit vollständiger Bezahlung der vereinbarten Vergütung räume ich dem Kunden das nicht ausschließliche Recht ein, die vertragsgegenständliche Software im vereinbarten Umfang zu nutzen.' },
      { heading: '7. Gewährleistung', text: 'Ich gewährleiste, dass die Software die vereinbarte Beschaffenheit hat und sich für die vertraglich vorausgesetzte Verwendung eignet. Die Gewährleistungsfrist beträgt 12 Monate.' },
      { heading: '8. Haftung', text: 'Ich hafte unbeschränkt bei Vorsatz oder grober Fahrlässigkeit, für die Verletzung von Leben, Leib oder Gesundheit, nach den Vorschriften des Produkthaftungsgesetzes sowie im Umfang einer von mir übernommenen Garantie.' },
      { heading: '9. Datenschutz', text: 'Ich verarbeite personenbezogene Daten der Kunden zweckgebunden und gemäß den gesetzlichen Bestimmungen. Die Einzelheiten ergeben sich aus meiner Datenschutzerklärung.' },
      { heading: '10. Schlussbestimmungen', text: 'Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des UN-Kaufrechts. Erfüllungsort und ausschließlicher Gerichtsstand ist Düsseldorf.' },
      { heading: '', text: 'Sollten einzelne Bestimmungen dieser AGB unwirksam sein oder werden, so berührt dies die Wirksamkeit der übrigen Bestimmungen nicht.' },
    ],
  },
  en: {
    title: 'Terms and Conditions',
    sections: [
      { heading: '1. Scope', text: 'These terms and conditions apply to all current and future business relationships between BrainMotion.ai and my clients.' },
      { heading: '2. Conclusion of Contract', text: 'The contract is concluded by my acceptance of the customer order. Acceptance can be declared either in writing or by delivery of the goods or provision of the service.' },
      { heading: '3. Scope of Services', text: 'The scope of contractual services is determined by the respective product description at the time of order or the individual offer.' },
      { heading: '4. Prices and Payment Terms', text: 'All prices are in euros plus statutory VAT. Payments are due within 14 days of invoicing without deduction.' },
      { heading: '5. Delivery and Performance Time', text: 'Delivery is made to the delivery address provided by the customer. Delivery dates are non-binding unless expressly agreed otherwise.' },
      { heading: '6. Rights of Use', text: 'Upon full payment of the agreed remuneration, I grant the customer the non-exclusive right to use the contractual software to the agreed extent.' },
      { heading: '7. Warranty', text: 'I warrant that the software has the agreed quality and is suitable for the contractually intended use. The warranty period is 12 months.' },
      { heading: '8. Liability', text: 'I am liable without limitation in cases of intent or gross negligence, for injury to life, body or health, under the provisions of the Product Liability Act, and to the extent of a guarantee assumed by me.' },
      { heading: '9. Data Protection', text: 'I process personal data of customers for specific purposes and in accordance with legal requirements. Details can be found in my privacy policy.' },
      { heading: '10. Final Provisions', text: 'The law of the Federal Republic of Germany applies, excluding the UN Convention on Contracts for the International Sale of Goods. Place of performance and exclusive place of jurisdiction is Düsseldorf.' },
      { heading: '', text: 'Should individual provisions of these terms and conditions be or become invalid, this shall not affect the validity of the remaining provisions.' },
    ],
  },
};

const Terms: React.FC = () => {
  const { language } = useLanguage();
  const t = termsContent[language];
  return (
    <TermsSection>
      <Container>
        <Title
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {t.title}
        </Title>
        <Content
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {t.sections.map((section, idx) => (
            <React.Fragment key={idx}>
              {section.heading && <h2>{section.heading}</h2>}
              <p>{section.text}</p>
            </React.Fragment>
          ))}
        </Content>
      </Container>
    </TermsSection>
  );
};

export default Terms; 